import { useEffect, useRef, useState } from 'react';
import { Copy, Download, Info, Loader2, Printer } from 'lucide-react';
import { getSceneFlagArt, SCENE_FLAG_URL } from '@generated/server/sceneFlag';
import { getStaffPass } from '@generated/hooks/staffPass';

const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
/** Next 14 issues starting November 2026, as "NOVEMBER '26". */
const EDITIONS = Array.from({ length: 14 }, (_, k) => { const m = (10 + k) % 12; const y = 26 + Math.floor((10 + k) / 12); return `${MONTHS[m]} '${y}`; });
const INK = '#16181d', PAPER = '#f6f1e4', RED = '#e0241b';

type Mode = 'band' | 'over';

/** Staff tab: the monthly "The Scene" header flag with the edition date, exportable on demand. */
export function SceneFlag() {
  const [art, setArt] = useState<HTMLImageElement | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [edition, setEdition] = useState(EDITIONS[0] ?? "NOVEMBER '26");
  const [custom, setCustom] = useState('');
  const [mode, setMode] = useState<Mode>('band');
  const [pos, setPos] = useState(88); // % down the artwork (overlay mode)
  const [size, setSize] = useState(7); // % of artwork width
  const [boxed, setBoxed] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const label = (custom.trim() || edition).toUpperCase();

  useEffect(() => {
    let live = true;
    getSceneFlagArt({ data: { pass: getStaffPass() } })
      .then((r) => { const img = new Image(); img.onload = () => { if (live) setArt(img); }; img.onerror = () => live && setLoadErr('The flag artwork could not be opened.'); img.src = r.dataUrl; })
      .catch((e: unknown) => { console.error(e); if (live) setLoadErr('The flag artwork could not be loaded right now. Please try again in a minute.'); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const c = canvasRef.current; if (!c || !art) return;
    let live = true;
    const draw = async () => {
      try { await document.fonts.load('400 100px Candal'); } catch { /* falls back to sans-serif */ }
      if (!live) return;
      const W = art.naturalWidth, H = art.naturalHeight;
      const band = mode === 'band' ? Math.round(W * 0.11) : 0;
      c.width = W; c.height = H + band;
      const g = c.getContext('2d'); if (!g) return;
      g.drawImage(art, 0, 0, W, H);
      const fs = Math.round(W * (size / 100));
      g.font = `400 ${fs}px Candal, "Arial Black", sans-serif`;
      g.textAlign = 'center'; g.textBaseline = 'middle';
      if (mode === 'band') {
        g.fillStyle = INK; g.fillRect(0, H, W, band);
        g.fillStyle = RED; g.fillRect(0, H, W, Math.max(4, Math.round(band * 0.06)));
        g.fillStyle = PAPER; g.fillText(label, W / 2, H + band * 0.55, W * 0.92);
      } else {
        const y = H * (pos / 100);
        const tw = Math.min(g.measureText(label).width, W * 0.92);
        if (boxed) { const px = fs * 0.5, py = fs * 0.32; g.fillStyle = INK; g.fillRect(W / 2 - tw / 2 - px, y - fs / 2 - py, tw + px * 2, fs + py * 2); }
        g.fillStyle = boxed ? PAPER : INK; g.fillText(label, W / 2, y, W * 0.92);
      }
    };
    void draw();
    return () => { live = false; };
  }, [art, label, mode, pos, size, boxed]);

  const fileName = `the-scene-flag_${label.replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase()}.png`;
  const blob = () => new Promise<Blob | null>((ok) => canvasRef.current?.toBlob(ok, 'image/png') ?? ok(null));

  const download = async () => {
    setBusy(true);
    try { const b = await blob(); if (!b) throw new Error('no image'); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = fileName; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); }
    catch (e) { console.error(e); setNote('The graphic could not be downloaded.'); } finally { setBusy(false); }
  };
  const printPdf = () => {
    const c = canvasRef.current; if (!c) return;
    const w = window.open('', '_blank', 'width=1000,height=800'); if (!w) { setNote('Allow pop-ups to print.'); return; }
    w.document.write(`<!doctype html><html><head><title>The Scene · ${label}</title><style>@page{size:landscape;margin:.4in}body{margin:0}img{width:100%;height:auto}</style></head><body><img src="${c.toDataURL('image/png')}" alt="The Scene ${label}"></body></html>`);
    w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 500);
  };
  const copyImage = async () => {
    try { const b = await blob(); if (!b || typeof ClipboardItem === 'undefined') throw new Error('unsupported'); await navigator.clipboard.write([new ClipboardItem({ 'image/png': b })]); setNote('Image copied. Paste it into your layout or email.'); }
    catch { try { await navigator.clipboard.writeText(SCENE_FLAG_URL); setNote('Copying the image isn’t supported here, so the artwork link was copied instead.'); } catch { setNote('Could not copy.'); } }
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(SCENE_FLAG_URL); setNote('Artwork link copied.'); } catch { setNote('Could not copy the link.'); } };

  const btn = 'inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50';
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0">
        <div className="border-2 border-foreground bg-[hsl(var(--paper))] p-3">
          {!art && !loadErr && <div className="flex aspect-[3/1] items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading the flag artwork…</div>}
          {loadErr && <p role="alert" className="p-6 text-sm text-destructive">{loadErr}</p>}
          <canvas ref={canvasRef} aria-label={`The Scene flag, ${label} edition`} className={`block h-auto w-full max-w-full ${art ? '' : 'hidden'}`} />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="inline-flex min-h-11 items-center gap-2 bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50" disabled={!art || busy} onClick={() => void download()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download high-res PNG</button>
          <button type="button" className={btn} disabled={!art} onClick={printPdf}><Printer className="h-4 w-4" />Print / save as PDF</button>
          <button type="button" className={btn} disabled={!art} onClick={() => void copyImage()}><Copy className="h-4 w-4" />Copy image</button>
          <button type="button" className={btn} onClick={() => void copyLink()}><Copy className="h-4 w-4" />Copy direct link</button>
        </div>
        {note && <p role="status" className="mt-2 text-sm font-semibold">{note}</p>}
        <p className="mt-4 flex gap-2 border-l-4 border-primary bg-muted/50 p-3 text-sm"><Info className="mt-0.5 h-4 w-4 shrink-0" /><span>This is the monthly header flag for <strong>The Scene</strong>. Run it above or beside the printed Scene calendar (from the Print Guide tab), or use it as a promotional banner. The PNG is exported at the artwork’s full resolution. The direct link is the base artwork without the edition date.</span></p>
      </div>
      <aside className="space-y-4">
        <label className="block text-sm font-semibold">Edition
          <select value={edition} onChange={(e) => { setEdition(e.target.value); setCustom(''); }} className="mt-1 block min-h-11 w-full border border-input bg-background px-2">{EDITIONS.map((e) => <option key={e} value={e}>{e}</option>)}</select>
        </label>
        <label className="block text-sm font-semibold">Or custom text
          <input value={custom} maxLength={28} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. HOLIDAY '26" className="mt-1 block min-h-11 w-full border border-input bg-background px-2 uppercase" />
        </label>
        <fieldset className="text-sm"><legend className="font-semibold">Date placement</legend>
          <label className="mt-1 flex min-h-11 items-center gap-2"><input type="radio" name="mode" checked={mode === 'band'} onChange={() => setMode('band')} />Band under the artwork</label>
          <label className="flex min-h-11 items-center gap-2"><input type="radio" name="mode" checked={mode === 'over'} onChange={() => setMode('over')} />On the artwork (covers any printed date)</label>
        </fieldset>
        {mode === 'over' && <>
          <label className="block text-sm font-semibold">Height on the flag ({pos}%)<input type="range" min={5} max={95} value={pos} onChange={(e) => setPos(Number(e.target.value))} className="mt-1 block w-full" /></label>
          <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={boxed} onChange={(e) => setBoxed(e.target.checked)} />Dark box behind the date</label>
        </>}
        <label className="block text-sm font-semibold">Date size ({size}%)<input type="range" min={3} max={14} value={size} onChange={(e) => setSize(Number(e.target.value))} className="mt-1 block w-full" /></label>
      </aside>
    </div>
  );
}
